import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { PROFILE_LIMITS, sanitizeUsername } from '@shared/types';
import { useSupabaseAuth } from '../hooks/useSupabaseAuth';
import { Navbar } from '../components/Layout/Navbar';
import { initialAuthRedirect } from '../lib/supabase';
import { translateAuthError } from '../lib/authErrors';

type Mode = 'signin' | 'signup' | 'forgot';

const MIN_PASSWORD = 6;

/**
 * Inicio de sesión, registro, recuperación de contraseña y acceso como
 * invitado (Supabase Auth vía `useSupabaseAuth`).
 *
 * Tras registrarse, Supabase envía un correo de confirmación cuyo enlace
 * vuelve a `/login`; el cliente procesa la sesión del enlace y esta
 * página redirige al Hub.
 */
export const LoginPage = () => {
  const navigate = useNavigate();
  const {
    player,
    isGuest,
    loading,
    error,
    notice,
    unconfirmedEmail,
    signInWithEmail,
    signUpWithEmail,
    resendConfirmation,
    requestPasswordReset,
    clearMessages,
    signInAnonymously,
  } = useSupabaseAuth();

  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // Error del enlace de correo (p. ej. caducado), una sola vez al entrar.
  const [linkError] = useState(() =>
    initialAuthRedirect.errorCode
      ? translateAuthError({
          message: initialAuthRedirect.errorDescription ?? initialAuthRedirect.errorCode,
          code: initialAuthRedirect.errorCode,
        })
      : null
  );

  // Mensajes de otra pantalla no deben aparecer aquí.
  useEffect(() => {
    clearMessages();
  }, [clearMessages]);

  const switchMode = (next: Mode) => {
    setMode(next);
    setFormError(null);
    clearMessages();
  };

  const validate = (): string | null => {
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return 'Escribe un email válido.';
    if (mode === 'forgot') return null;
    if (password.length < MIN_PASSWORD) return `La contraseña debe tener al menos ${MIN_PASSWORD} caracteres.`;
    if (mode === 'signup' && !sanitizeUsername(username)) {
      return `El nombre de usuario debe tener entre ${PROFILE_LIMITS.usernameMin} y ${PROFILE_LIMITS.usernameMax} caracteres.`;
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const invalid = validate();
    setFormError(invalid);
    if (invalid) return;
    const cleanEmail = email.trim();
    // Al iniciar sesión con éxito, `player` deja de ser invitado y el
    // render redirige al Hub; si falla, `error` se muestra aquí.
    if (mode === 'signin') await signInWithEmail(cleanEmail, password);
    else if (mode === 'signup') await signUpWithEmail(cleanEmail, password, sanitizeUsername(username) ?? username);
    else await requestPasswordReset(cleanEmail);
  };

  if (player && !isGuest) {
    return <Navigate to="/" replace />;
  }

  const shownError = formError ?? error ?? linkError;

  return (
    <div className="min-h-screen bg-ink-900">
      <Navbar player={null} />
      <main className="mx-auto grid max-w-md gap-6 px-4 py-10 sm:px-6 sm:py-16">
        <header className="text-center">
          <h1 className="text-display text-3xl font-bold text-white">
            {mode === 'forgot' ? 'Recuperar contraseña' : 'Bienvenido a SportsArena'}
          </h1>
          <p className="mt-2 text-sm text-slate-400">
            {mode === 'forgot'
              ? 'Te enviaremos un enlace para crear una contraseña nueva.'
              : 'La plataforma de deportes hiperrealistas.'}
          </p>
        </header>

        <form onSubmit={handleSubmit} noValidate className="card-surface space-y-4 p-6">
          {mode !== 'forgot' && (
            <div className="flex rounded-lg border border-white/10 bg-ink-700/60 p-1 text-sm">
              <TabButton active={mode === 'signin'} onClick={() => switchMode('signin')}>
                Iniciar sesión
              </TabButton>
              <TabButton active={mode === 'signup'} onClick={() => switchMode('signup')}>
                Registrarme
              </TabButton>
            </div>
          )}

          {mode === 'signup' && (
            <Field
              label="Nombre de usuario"
              value={username}
              onChange={setUsername}
              placeholder="TuNickname"
              autoComplete="username"
            />
          )}
          <Field
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="tu@email.com"
            autoComplete="email"
          />
          {mode !== 'forgot' && (
            <Field
              label="Contraseña"
              type="password"
              value={password}
              onChange={setPassword}
              placeholder={mode === 'signup' ? `Mínimo ${MIN_PASSWORD} caracteres` : '••••••••'}
              autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
            />
          )}

          {mode === 'signin' && (
            <div className="-mt-2 text-right">
              <button
                type="button"
                onClick={() => switchMode('forgot')}
                className="text-xs font-semibold text-accent-400 hover:underline"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          )}

          {notice && (
            <p role="status" className="rounded-md border border-emerald-400/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">
              {notice}
            </p>
          )}
          {shownError && (
            <p role="alert" className="rounded-md border border-neon-red/40 bg-neon-red/10 px-3 py-2 text-xs text-neon-red">
              {shownError}
            </p>
          )}
          {unconfirmedEmail && (
            <button
              type="button"
              disabled={loading}
              onClick={() => void resendConfirmation(unconfirmedEmail)}
              className="btn-ghost h-9 w-full text-xs"
            >
              Reenviar correo a {unconfirmedEmail}
            </button>
          )}

          <button type="submit" disabled={loading} className="btn-primary w-full">
            {loading
              ? 'Cargando…'
              : mode === 'signin'
                ? 'Entrar'
                : mode === 'signup'
                  ? 'Crear cuenta'
                  : 'Enviar enlace'}
          </button>

          {mode === 'forgot' ? (
            <button type="button" onClick={() => switchMode('signin')} className="btn-ghost w-full">
              ← Volver a iniciar sesión
            </button>
          ) : (
            <>
              <div className="relative py-2 text-center text-xs text-slate-500">
                <span className="bg-ink-800 px-2">o</span>
              </div>
              <button
                type="button"
                onClick={async () => {
                  await signInAnonymously();
                  navigate('/');
                }}
                className="btn-ghost w-full"
              >
                Jugar como invitado
              </button>
            </>
          )}
        </form>

        <p className="text-center text-xs text-slate-500">
          <Link to="/" className="hover:underline">
            ← Volver al Hub
          </Link>
        </p>
      </main>
    </div>
  );
};

const TabButton = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex-1 rounded-md px-3 py-1.5 font-semibold transition ${
      active ? 'bg-accent-500 text-ink-900' : 'text-slate-300'
    }`}
  >
    {children}
  </button>
);

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  autoComplete?: string;
}

export const Field = ({ label, value, onChange, placeholder, type = 'text', autoComplete }: FieldProps) => (
  <label className="block">
    <span className="mb-1 block text-xs font-semibold text-slate-300">{label}</span>
    <input
      type={type}
      value={value}
      autoComplete={autoComplete}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-lg border border-white/10 bg-ink-700/60 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 outline-none transition focus:border-accent-500 focus:ring-2 focus:ring-accent-500/30"
    />
  </label>
);
